import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Edit, Delete, MoreVertical, X, Search, User, Filter, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import EditUser from "../EditUser";
import logo from "../../assets/placeholder.jpg";

export default function AllUsers({ theme }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [workingId, setWorkingId] = useState(null);
  const [toast, setToast] = useState({ type: "", message: "" });
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [editingUserId, setEditingUserId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [modalPhoto, setModalPhoto] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/users`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!res.ok) throw new Error("Failed to fetch users");
      const data = await res.json();

      const mapped = data.map((user) => ({
        id: user.id,
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        role: user.role || "",
        status: user.status || "Inactive",
        createdDate: user.createdDate || "N/A",
        createdBy: user.createdBy || "N/A",
        profilePhoto: user.profilePhoto || logo,
      }));

      setUsers(mapped);
    } catch (err) {
      console.error(err);
      setToast({ type: "error", message: "Failed to load users." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (toast.message) {
      const timer = setTimeout(() => setToast({ type: "", message: "" }), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setModalPhoto(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const filteredUsers = useMemo(() => {
    let result = users.filter((u) =>
      `${u.firstName} ${u.lastName} ${u.email}`
        .toLowerCase()
        .includes(searchFilter.toLowerCase())
    );

    if (roleFilter) {
      result = result.filter((u) => u.role === roleFilter);
    }

    // Sort by createdDate - most recent first
    result.sort((a, b) => {
      const dateA = new Date(a.createdDate || 0).getTime();
      const dateB = new Date(b.createdDate || 0).getTime();
      return dateB - dateA;
    });

    return result;
  }, [users, searchFilter, roleFilter]);

  // Get unique roles for filter dropdown
  const uniqueRoles = useMemo(() => {
    return [...new Set(users.map((u) => u.role))].filter(Boolean).sort();
  }, [users]);

  const updateStatus = async (userId, newStatus) => {
    setWorkingId(userId);
    const oldStatus = users.find((u) => u.id === userId)?.status ?? "Inactive";

    setUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, status: newStatus } : u))
    );

    try {
      const isActive = newStatus === "Active" ? 1 : 0;
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ is_active: isActive }),
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Status update failed.");
      setToast({ type: "success", message: "Status updated successfully." });
    } catch (err) {
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, status: oldStatus } : u))
      );
      setToast({
        type: "error",
        message: err.message || "Status update failed.",
      });
    } finally {
      setWorkingId(null);
    }
  };

  const deleteUser = async (userId) => {
    setWorkingId(userId);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/catalog/users/${userId}`;

      const res = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await res.text();
        console.error("Non-JSON response received:", text);
        throw new Error(
          `Server returned non-JSON response. Status: ${res.status}`
        );
      }

      const data = await res.json();
      if (!res.ok)
        throw new Error(
          data?.message || `Delete failed with status: ${res.status}`
        );

      setUsers((prev) => prev.filter((u) => u.id !== userId));
      setToast({
        type: "success",
        message: data?.message || "User deleted successfully.",
      });
    } catch (err) {
      console.error("Delete error:", err);
      setToast({ type: "error", message: err.message || "Delete failed." });
    } finally {
      setWorkingId(null);
      setDropdownOpenId(null);
      setConfirmDelete(null);
    }
  };

  const handleDeleteClick = (user) => {
    setConfirmDelete({
      id: user.id,
      name: `${user.firstName} ${user.lastName}`,
    });
    setDropdownOpenId(null);
  };

  const onEditSave = (updatedUser) => {
    setUsers((prev) =>
      prev.map((u) => (u.id === updatedUser.id ? updatedUser : u))
    );
    setEditingUserId(null);
    setToast({ type: "success", message: "User updated successfully." });
  };

  const handleEditClick = (userId) => {
    setEditingUserId(userId);
    setDropdownOpenId(null);
  };

  const closeEditModal = () => {
    setEditingUserId(null);
  };

  if (loading) {
    return (
      <div className="w-full h-64 flex items-center justify-center bg-white">
        <motion.div
          className="flex gap-2"
          initial="hidden"
          animate="visible"
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.15,
                repeat: Infinity,
                repeatType: "loop",
              },
            },
          }}
        >
          {[...Array(3)].map((_, i) => (
            <motion.span
              key={i}
              className="w-2 h-2 bg-blue-600 rounded-full"
              variants={{
                hidden: { scale: 0.5, opacity: 0.5 },
                visible: {
                  scale: [0.6, 1.1, 0.6],
                  opacity: [0.5, 1, 0.5],
                  transition: {
                    duration: 0.6,
                    repeat: Infinity,
                    ease: "easeInOut",
                  },
                },
              }}
            />
          ))}
        </motion.div>
      </div>
    );
  }

  const userToEdit = editingUserId
    ? users.find((u) => u.id === editingUserId)
    : null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 max-w-7xl mx-auto mt-4 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">
              User Management
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Manage system users and permissions
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 flex-1 max-w-lg">
            {/* Search Box */}
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-3.5 w-3.5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="Search users..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
              />
            </div>

            {/* Role Filter Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm font-medium text-gray-700 whitespace-nowrap"
              >
                <Filter className="h-3.5 w-3.5 text-gray-500" />
                Role
                <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${showRoleDropdown ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {showRoleDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 z-20 mt-1 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5"
                  >
                    <div className="py-1 max-h-64 overflow-y-auto">
                      <button
                        onClick={() => {
                          setRoleFilter("");
                          setShowRoleDropdown(false);
                        }}
                        className={`flex w-full items-center px-4 py-2 text-sm ${
                          roleFilter === ""
                            ? "bg-blue-50 text-blue-700 font-medium"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        All Roles
                      </button>
                      {uniqueRoles.map((role) => (
                        <button
                          key={role}
                          onClick={() => {
                            setRoleFilter(role);
                            setShowRoleDropdown(false);
                          }}
                          className={`flex w-full items-center px-4 py-2 text-sm ${
                            roleFilter === role
                              ? "bg-blue-50 text-blue-700 font-medium"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {role}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-gray-600">
            Showing <span className="font-semibold text-gray-800">{filteredUsers.length}</span> of{" "}
            <span className="font-semibold text-gray-800">{users.length}</span> users
          </span>
          {(searchFilter || roleFilter) && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              📌 {searchFilter && `Search: "${searchFilter}"`} {searchFilter && roleFilter && "•"} {roleFilter && `Role: ${roleFilter}`}
            </span>
          )}
        </div>
        <div className="flex gap-4 flex-wrap">
          <div className="flex items-center">
            <div className="w-2 h-2 rounded-full bg-green-500 mr-1.5"></div>
            <span className="text-gray-600">
              Active: <span className="font-semibold">{filteredUsers.filter((u) => u.status === "Active").length}</span>
            </span>
          </div>
          <div className="flex items-center">
            <div className="w-2 h-2 rounded-full bg-red-500 mr-1.5"></div>
            <span className="text-gray-600">
              Inactive: <span className="font-semibold">{filteredUsers.filter((u) => u.status === "Inactive").length}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      <div className="fixed top-3 right-3 z-50 space-y-2">
        <AnimatePresence>
          {toast.message && (
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.2 }}
              className={`max-w-xs w-full ${
                toast.type === "error"
                  ? "bg-red-50 border-red-200 text-red-700"
                  : "bg-green-50 border-green-200 text-green-700"
              } border rounded-md shadow-sm overflow-hidden text-sm`}
            >
              <div className="flex items-start p-3">
                <div className="flex-1">{toast.message}</div>
                <button
                  className="ml-2 text-gray-400 hover:text-gray-600 text-xs"
                  onClick={() => setToast({ type: "", message: "" })}
                >
                  ×
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Users Table with Scrolling */}
      <div className="overflow-hidden">
        <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-400px)] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <table className="w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Created By
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Created Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center bg-white">
                    <div className="flex flex-col items-center justify-center">
                      <Search className="h-12 w-12 text-gray-300 mb-3" />
                      <p className="text-gray-600 text-sm font-medium">No users found</p>
                      <p className="text-gray-400 text-xs mt-1">
                        {searchFilter || roleFilter ? "Try adjusting your filters" : "Start by adding a new user"}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <motion.tr
                    key={user.id}
                    className="hover:bg-blue-50 transition-colors"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          {user.profilePhoto ? (
                            <img
                              src={user.profilePhoto}
                              alt={`${user.firstName} ${user.lastName}`}
                              className="h-8 w-8 rounded-full object-cover cursor-pointer hover:opacity-80"
                              onClick={() => setModalPhoto(user.profilePhoto)}
                            />
                          ) : (
                            <div className="bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full w-8 h-8 flex items-center justify-center">
                              <User className="h-4 w-4 text-blue-700" />
                            </div>
                          )}
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-semibold text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-xs text-gray-500">
                            ID: {user.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-700">{user.email}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="inline-flex px-3 py-1 text-xs leading-5 font-semibold rounded-full bg-gradient-to-r from-blue-100 to-blue-50 text-blue-800 border border-blue-200">
                        {user.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {user.createdBy || "N/A"}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-xs text-gray-600">
                        {user.createdDate && user.createdDate !== "N/A"
                          ? new Date(user.createdDate).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                          : "N/A"}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="relative inline-block w-9 mr-2 align-middle select-none">
                          <input
                            type="checkbox"
                            checked={user.status === "Active"}
                            onChange={(e) =>
                              updateStatus(
                                user.id,
                                e.target.checked ? "Active" : "Inactive"
                              )
                            }
                            disabled={workingId === user.id}
                            className="sr-only"
                            id={`toggle-${user.id}`}
                          />
                          <label
                            htmlFor={`toggle-${user.id}`}
                            className={`block overflow-hidden h-5 rounded-full cursor-pointer transition-colors ${
                              user.status === "Active"
                                ? "bg-green-500"
                                : "bg-gray-300"
                            }`}
                          >
                            <span
                              className={`block h-3 w-3 mt-1 rounded-full bg-white transform transition-transform duration-300 ease-in-out ${
                                user.status === "Active"
                                  ? "translate-x-4"
                                  : "translate-x-1"
                              }`}
                            ></span>
                          </label>
                        </div>
                        <span className={`text-xs font-semibold ${
                          user.status === "Active" ? "text-green-600" : "text-gray-600"
                        }`}>
                          {user.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end">
                        <div className="relative">
                          <button
                            onClick={() =>
                              setDropdownOpenId((prev) =>
                                prev === user.id ? null : user.id
                              )
                            }
                            className="text-gray-400 hover:text-gray-600 focus:outline-none p-1.5 rounded hover:bg-gray-100 transition-colors"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>

                          <AnimatePresence>
                            {dropdownOpenId === user.id && (
                              <motion.div
                                initial={{ opacity: 0, y: -5 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -5 }}
                                transition={{ duration: 0.15 }}
                                className="absolute right-0 z-20 mt-1 w-40 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
                              >
                                <div className="py-1">
                                  <button
                                    onClick={() => handleEditClick(user.id)}
                                    className="flex w-full items-center px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                                  >
                                    <Edit className="mr-2 h-3.5 w-3.5 text-gray-500" />
                                    Edit User
                                  </button>
                                  <button
                                    onClick={() => handleDeleteClick(user)}
                                    className="flex w-full items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                  >
                                    <Delete className="mr-2 h-3.5 w-3.5 text-red-500" />
                                    Delete User
                                  </button>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Custom Scrollbar CSS */}
      <style>{`
        .scrollbar-thin::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: #f3f4f6;
          border-radius: 10px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 10px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>

      {/* Edit User Modal */}
      <AnimatePresence>
        {editingUserId && userToEdit && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeEditModal}
          >
            <motion.div
              className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[85vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold">Edit User</h3>
                <button
                  onClick={closeEditModal}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="p-4">
                <EditUser
                  user={userToEdit}
                  onCancel={closeEditModal}
                  onSave={onEditSave}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-lg shadow-xl max-w-xs w-full"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ duration: 0.2 }}
            >
              <div className="p-4">
                <h3 className="text-base font-semibold mb-3">Confirm Delete</h3>
                <p className="text-sm mb-4">
                  Delete{" "}
                  <span className="font-semibold">{confirmDelete.name}</span>?
                </p>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => setConfirmDelete(null)}
                    className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded text-sm hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => deleteUser(confirmDelete.id)}
                    className="bg-red-600 text-white px-3 py-1.5 rounded text-sm hover:bg-red-700 transition-colors"
                    disabled={workingId === confirmDelete.id}
                  >
                    {workingId === confirmDelete.id ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Photo Modal */}
      <AnimatePresence>
        {modalPhoto && (
          <motion.div
            key="photo-modal"
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setModalPhoto(null)}
          >
            <motion.div
              className="relative"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={modalPhoto}
                alt="Profile"
                className="max-h-[80vh] max-w-[80vw] rounded object-contain"
              />
              <button
                onClick={() => setModalPhoto(null)}
                className="absolute top-2 right-2 bg-black/50 hover:bg-black/75 rounded-full p-1 text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
